// eslint-disable-next-line @typescript-eslint/ban-types
export type OverridableStringUnion<T, U = {}> = GenerateStringUnion<
  Overwrite<T, U>
>;

export type Overwrite<T, U> = Omit<T, keyof U> & U;

type GenerateStringUnion<T> = Extract<
  {
    [Key in keyof T]: true extends T[Key] ? Key : never;
  }[keyof T],
  string
>;

export type BreakpointDefaults = Record<
  'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'kambi',
  true
>;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BreakpointOverrides {}

export type Breakpoint = OverridableStringUnion<
  BreakpointDefaults,
  BreakpointOverrides
>;
export type BreakpointValues = { [key in Breakpoint]: number };
export type keys = Breakpoint[];

export type BreakpointFn = (key: Breakpoint) => string;
export type Breakpoints = {
  keys: Breakpoint[];
  values: BreakpointValues;
  up: BreakpointFn;
  down: BreakpointFn;
  between: (start: Breakpoint, end: Breakpoint) => string;
  only: BreakpointFn;
  width: (key: Breakpoint) => number;
  unit: string;
};

export type BreakpointsOptions = Partial<
  {
    step: number;
  } & Breakpoints
>;

// Sorted ASC by size. That's important.
// It can't be configured as it's used statically for propTypes.
export const breakpointKeys = ['xs', 'sm', 'md', 'lg', 'xl', 'kambi'];

// Keep in mind that @media is inclusive by the CSS specification.
export default function createBreakpoints(
  breakpoints: BreakpointsOptions
): Breakpoints {
  const {
    // The breakpoint **start** at this value.
    // For instance with the first breakpoint xs: [xs, sm).
    values = {
      xxs: 0,
      xs: 374,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      kambi: 670,
    },
    unit = 'px',
    step = 5,
    ...other
  } = breakpoints;

  const keys = Object.keys(values) as Breakpoint[];

  function up(key: Breakpoint) {
    const value = typeof values[key] === 'number' ? values[key] : key;
    return `@media (min-width:${value}${unit})`;
  }

  function down(key: Breakpoint) {
    const value = typeof values[key] === 'number' ? values[key] : key;
    return `@media (max-width:${(value as number) - step / 100}${unit})`;
  }

  function between(start: Breakpoint, end: Breakpoint) {
    const endIndex = keys.indexOf(end);

    return (
      `@media (min-width:${
        typeof values[start] === 'number' ? values[start] : start
      }${unit}) and ` +
      `(max-width:${
        ((endIndex !== -1 && typeof values[keys[endIndex]] === 'number'
          ? values[keys[endIndex]]
          : end) as number) -
        step / 100
      }${unit})`
    );
  }

  function only(key: Breakpoint) {
    if (keys.indexOf(key) + 1 < keys.length) {
      return between(key, keys[keys.indexOf(key) + 1]);
    }

    return up(key);
  }

  function width(key: Breakpoint) {
    return values[key];
  }

  return {
    keys,
    values,
    up,
    down,
    between,
    only,
    width,
    unit,
    ...other,
  };
}
